import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from './apiUtils';
import { sanitizeRetailerCode, validateRetailerCode, sanitizeInput } from './authUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './components/ui/dialog';
import { Trash2, CheckCircle, AlertCircle, Store as StoreIcon, Building2, Phone, Hash } from 'lucide-react';
import { toast } from 'sonner';

function AddStore() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, store: null });
  
  const [formData, setFormData] = useState({
    shopName: '',
    retailerCode: '',
    contact: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchStores = async () => {
    setLoading(true);
    setError('');
    
    // Token no longer needed - apiUtils handles authentication automatically

    try {
      // Use the unified /api/stores endpoint
      const response = await apiGet('/api/stores');

      if (!response.ok) {
        throw new Error('Failed to fetch stores');
      }

      const storesData = await response.json();
      console.log('Stores data from API:', storesData);
      
      // The API already includes TGBCL, so just use the response directly
      setStores(storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
      setError('Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Handle different input types with proper sanitization
    if (name === 'retailerCode') {
      processedValue = sanitizeRetailerCode(value);
    } else if (name === 'shopName') {
      processedValue = sanitizeInput(value);
    } else if (name === 'contact') {
      // Keep contact as digits only, max 10
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const validateForm = () => {
    if (!formData.shopName.trim()) {
      setError('Shop name is required');
      return false;
    }
    if (!validateRetailerCode(formData.retailerCode)) {
      setError('Retailer code must be exactly 7 digits');
      return false;
    }
    if (!formData.contact || formData.contact.length !== 10) {
      setError('Contact must be exactly 10 digits');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      // Token no longer needed - apiUtils handles authentication automatically

      await apiPost('/api/stores', {
        shopName: formData.shopName.trim(),
        retailerCode: formData.retailerCode,
        contact: formData.contact
      });
      setSuccess('Store added successfully!');
      resetForm();
      fetchStores();
    } catch (error) {
      console.error('Error adding store:', error);
      setError('Error adding store');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      shopName: '',
      retailerCode: '',
      contact: ''
    });
  };

  const handleDelete = (storeId, store) => {
    if (storeId === 'tgbcl') {
      setError('Cannot delete the default TGBCL store');
      return;
    }

    if (store.source === 'user_shop' || store.source === 'internal') {
      setError('Cannot delete internal stores (shops belonging to the same user).');
      return;
    }

    // Open confirmation dialog
    setDeleteDialog({ open: true, store: { ...store, id: storeId } });
  };

  const confirmDelete = async () => {
    const store = deleteDialog.store;
    setDeleteDialog({ open: false, store: null });
    
    if (!store) return;

    try {
      setLoading(true);
      const response = await apiDelete(`/api/stores/${store.id}`);

      if (response.ok) {
        toast.success('Store deleted successfully!');
        setSuccess('Store deleted successfully!');
        setError(''); // Clear any errors
        fetchStores();
      } else {
        const errorData = await response.json();
        const errorMsg = errorData.message || 'Failed to delete store';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error deleting store:', error);
      setError('Error deleting store');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-left">Add Store</h1>
          <p className="text-base text-muted-foreground text-left">Manage your store network and add external stores</p>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Add New Store Form */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <StoreIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <CardTitle className="text-left">Add New Store</CardTitle>
                <CardDescription className="text-left">Enter the details of the external store</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Shop Name */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="shopName" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Shop Name *
                  </Label>
                  <Input
                    type="text"
                    id="shopName"
                    name="shopName"
                    value={formData.shopName}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC Wine Shop"
                    required
                    className="text-base"
                  />
                </div>

                {/* Retailer Code */}
                <div className="space-y-2">
                  <Label htmlFor="retailerCode" className="flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    Retailer Code *
                  </Label>
                  <Input
                    type="text"
                    id="retailerCode"
                    name="retailerCode"
                    value={formData.retailerCode}
                    onChange={handleInputChange}
                    placeholder="7 digits (e.g., 1234567)"
                    maxLength="7"
                    required
                    className="font-mono text-base"
                  />
                  <p className="text-xs text-muted-foreground">Must be exactly 7 digits</p>
                </div>

                {/* Contact */}
                <div className="space-y-2">
                  <Label htmlFor="contact" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Contact Number *
                  </Label>
                  <Input
                    type="text"
                    id="contact"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    placeholder="10 digits (e.g., 9876543210)"
                    maxLength="10"
                    required
                    className="font-mono text-base"
                  />
                  <p className="text-xs text-muted-foreground">Must be exactly 10 digits</p>
                </div>
              </div>

              <div className="flex gap-4 justify-end pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={loading}
                  className="gap-2"
                >
                  Reset Form
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="gap-2"
                >
                  <StoreIcon className="h-4 w-4" />
                  {loading ? 'Adding...' : 'Add Store'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Existing Stores List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-left">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-left">Store Network</CardTitle>
                  <CardDescription className="text-left">
                    {stores.length} {stores.length === 1 ? 'store' : 'stores'} in your network
                  </CardDescription>
                </div>
              </div>
              <Badge variant="secondary" className="text-sm">
                Total: {stores.length}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading && stores.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mb-4"></div>
                <p className="text-muted-foreground">Loading stores...</p>
              </div>
            ) : stores.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <StoreIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No stores found. TGBCL will be added as default.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16 text-center">S.No</TableHead>
                      <TableHead className="text-left">Store Name</TableHead>
                      <TableHead className="text-left">Retailer Code</TableHead>
                      <TableHead className="text-left">Type</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stores.map((store, index) => {
                      const isDefault = store.id === 'tgbcl' || store.store_type === 'default' || store.store_type === 'internal';
                      return (
                        <TableRow key={store.id} className={isDefault ? 'bg-green-50/50' : ''}>
                          <TableCell className="font-medium text-center">{index + 1}</TableCell>
                          <TableCell className="text-left">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{store.shop_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-left">
                            <span className="font-mono text-sm">{store.retailer_code}</span>
                          </TableCell>
                          <TableCell className="text-left">
                            {isDefault ? (
                              <Badge variant="default" className="bg-green-600">
                                Default
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                External
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {isDefault ? (
                              <span className="text-xs text-muted-foreground">Protected</span>
                            ) : (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(store.id, store)}
                                title="Delete store"
                                className="gap-2"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Store</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this store?
              </DialogDescription>
            </DialogHeader>
            {deleteDialog.store && (
              <div className="py-4">
                <p><strong>Store Name:</strong> {deleteDialog.store.shop_name}</p>
                <p><strong>Retailer Code:</strong> {deleteDialog.store.retailer_code}</p>
                <p className="text-sm text-destructive mt-2">This action cannot be undone.</p>
              </div>
            )}
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setDeleteDialog({ open: false, store: null })}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
  );
}

export default AddStore;
